import React, { useEffect } from 'react';

import { Provider } from 'react-redux';
import { syncHistoryWithStore } from 'react-router-redux';
import { browserHistory, Route, Router } from 'react-router';

import { configStore } from '../store/configStore';
import { requireAuth } from './AuthContainer';

import App from './App';
import BookingRequestContainer from './BookingRequest';
import InboxMainContainer from './InboxContainer';
import BookingsMainContainer from './BookingsContainer';
import MessageMainContainer from './MessageContainer';
import AttendeePageMain from '../../app-attendee-page/attendee-page-main';
import AttendeeSendInvitePageMain from '../../app-attendee-send-invite-page/AttendeeSendInvitePageMain';
import AttendeeSendInvitePageShare from '../../app-attendee-send-invite-page/AttendeeSendInvitePageShare';
import AttendeeSendInvitePageBooking from '../../app-attendee-send-invite-page/AttendeeSendInvitePageBooking';

import ProjectsContainer from './ProjectsContainer';
import ProjectDetailsContainer from './ProjectDetailsContainer';
import ReferralContainer from './ReferralContainer';
import ReferralLinkContainer from './ReferralLinkContainer';

import UpdateBookingContainer from './UpdateBookingContainer';
import ReviewUpdateContainer from './ReviewUpdateContainer';
import BookingOvertimeContainer from './BookingOvertimeContainer';
import AttendeeAddDetailsPageBooking from '../../app-attendee-add-details-page/AttendeeAddDetailsPageBooking';
import AttendeeAddDetailsPageEdit from '../../app-attendee-add-details-page/AttendeeAddDetailsPageEdit';
import AttendeeAddDetailsPageMain from '../../app-attendee-add-details-page/AttendeeAddDetailsPageMain';
import AttendeeSetupRouterMain from '../../app-attendee-setup-router/attendee-setup-router-main';
import CancelBookingContainer from './CancelBookingContainer';
import CancellationSummaryContainer from './CancellationSummaryContainer';
import CustomOfferContainer from './CustomOfferContainer';
import ReviewOfferDetailsContainer from './ReviewOfferDetailsContainer';
import CurrentDetailsContainer from './CurrentDetailsContainer';
import AttendeeReviewContainer from './AttendeeReviewContainer';
import ReviewRequestContainer from './ReviewRequestContainer';
import SignupContainer from '../../app-auth/SignupMain';
import ProfileContainer from '../../app-auth/SignupProfile';
import VerifyContainer from '../../app-auth/SignupVerify';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import PaymentDetailsContainer from './PaymentDetailsContainer';
import UpdatePaymentContainer from './UpdatePaymentContainer';
import ReviewsContainer from '../../app-reviews/ReviewsContainer';
import InquiryContainer from './InquiryContainer';
import { CateringPageContainer } from './CateringPageContainer';
import { CateringLandingPage } from '../../catering-landing/CateringLandingPage';
import BookingRequestExperiment from './BookingRequestExperiment';
import HostBidsContainer from './HostBidsContainer';

const preloadedState = window.__PRELOADED_STATE__;

export const store = configStore(browserHistory, preloadedState);
const history = syncHistoryWithStore(browserHistory, store);

const RootMain = () => {
  useEffect(() => {
    history.listen(location => {
      window.analytics.page();

      // Scrolls user to top on location change, can add paths to exclude if needed
      window.scrollTo(0, 0);
    });
  }, []);

  return (
    <Provider store={store}>
      <SharedIntlProviderHOC>
        <Router history={history}>
          <Route path="/" component={App}>
            <Route path="bkc/:listingID(/:mobile)" component={requireAuth(BookingRequestExperiment)} />
            <Route path="add-services/:bookingID" component={requireAuth(BookingRequestContainer)} />
            <Route path="(:urlPrefix/)referrals/:referralId" component={SignupContainer} />
            <Route path="(:urlPrefix/)referrals/:referralId/profile" component={requireAuth(ProfileContainer)} />
            <Route path="(:urlPrefix/)referrals/:referralId/verify" component={requireAuth(VerifyContainer)} />
            <Route path="(:urlPrefix/)claim/:referralId" component={SignupContainer} />
            <Route path="referrals" component={requireAuth(ReferralContainer)} />
            <Route path="referrals/promotion/:bookingId" component={requireAuth(ReferralLinkContainer)} />
            <Route path="(:urlPrefix/)invite/:publicBookingId" component={AttendeePageMain} />
            <Route path="invite/router/:publicBookingId" component={requireAuth(AttendeeSetupRouterMain)} />
            <Route path="invite/send/:bookingId" component={requireAuth(AttendeeSendInvitePageMain)} />
            <Route path="invite/send/:bookingId/share" component={requireAuth(AttendeeSendInvitePageShare)} />
            <Route path="invite/send/:bookingId/booking" component={requireAuth(AttendeeSendInvitePageBooking)} />
            <Route path="invite/add/:bookingId" component={requireAuth(AttendeeAddDetailsPageMain)} />
            <Route path="invite/add/:bookingId/edit" component={requireAuth(AttendeeAddDetailsPageEdit)} />
            <Route path="invite/add/:bookingId/booking" component={requireAuth(AttendeeAddDetailsPageBooking)} />
            <Route path="review/attendee/:reviewId" component={requireAuth(AttendeeReviewContainer)} />
            <Route path="inbox" component={requireAuth(InboxMainContainer)} />
            <Route path="bookings" component={requireAuth(BookingsMainContainer)} />
            <Route path="inbox/:id" component={requireAuth(MessageMainContainer)} />
            <Route path="inbox/:bookingID/review-request" component={ requireAuth(ReviewRequestContainer) } />
            <Route path="inbox/:bookingId/catering" component={requireAuth(CateringPageContainer)} />
            <Route path="catering" component={CateringLandingPage} />
            <Route path="inbox/:id/offer-custom-price" component={ requireAuth(CustomOfferContainer) } />
            <Route path="inbox/:id/edit-offer" component={ requireAuth(CustomOfferContainer) } />
            <Route path="inbox/:bookingID/review-offer" component={ requireAuth(ReviewOfferDetailsContainer) } />
            <Route path="inbox/:bookingID/current-details" component={ requireAuth(CurrentDetailsContainer) } />
            <Route path="inbox/:id/guest-update-booking(/:step)" component={ requireAuth(UpdateBookingContainer) } />
            <Route path="inbox/:id/host-update-booking" component={ requireAuth(CustomOfferContainer) } />
            <Route path="inbox/:bookingID/review-booking-update" component={ requireAuth(ReviewUpdateContainer) } />
            <Route path="inbox/:bookingID/overtime" component={ requireAuth(BookingOvertimeContainer) } />
            <Route path="inbox/:id/cancel-booking" component={ requireAuth(CancelBookingContainer) } />
            <Route path="inbox/:id/cancellation-summary" component={ requireAuth(CancellationSummaryContainer) } />
            <Route path="(:urlPrefix/)boards" component={ requireAuth(ProjectsContainer) } />
            <Route path="(:urlPrefix/)boards/:externalID" component={ ProjectDetailsContainer } />
            <Route path="(:urlPrefix/)boards/:externalID/:properties" component={ ProjectDetailsContainer } />
            <Route path="add-services/production-insurance/:bookingId" component={requireAuth(BookingRequestContainer)} />
            <Route path="inbox/:bookingID/payment-details" component={ requireAuth(PaymentDetailsContainer) } />
            <Route path="inbox/:bookingID/update-payment" component={ requireAuth(UpdatePaymentContainer) } />
            <Route path="inquiry/:listingID" component={ requireAuth(InquiryContainer) } />
            <Route path="ps-promoted-by-listing-space" component={requireAuth(HostBidsContainer)} />
            <Route
              path="reviews(/:reviewTab)"
              component={ requireAuth(ReviewsContainer) }
            />
          </Route>
        </Router>
      </SharedIntlProviderHOC>
    </Provider>
  );
};

export default RootMain;
